import React, { FC } from 'react'
import {
  BoxProps,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'next-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import toast from '_app/utils/toast'
import { useOutOfStockNotificationMutation } from '_app/graphql/generated/graphql'
import Button from '_app/components/Buttons/Button'
import Input from '_app/components/Form/Input'
import useCustomer from '_app/hooks/useCustomer'

interface ProductNotificationProps extends BoxProps {
  productId: string;
}

const ProductNotification: FC<ProductNotificationProps> = ({
  children,
  productId,
}) => {
  const { t } = useTranslation('product')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { customer } = useCustomer()
  const schema = yup.object().shape({ email: yup.string().email() })
  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, formState: { isSubmitting } } = methods
  const [outOfStockNotification] = useOutOfStockNotificationMutation({
    onCompleted: data => {
      toast({ description: data?.outOfStockNotification?.success ?? '', status: 'success' })
      onClose()
    },
    onError: error => { toast({ description: error.message, status: 'warning' }) },
  })
  const onSubmit = handleSubmit(async ({ email }) => {
    const isLogged = !!customer?.email
    if (isLogged) {
      return outOfStockNotification({ variables: { email: customer.email ?? '', productId } })
    } else {
      onOpen()
      if (!!email)
        return outOfStockNotification({ variables: { email, productId } })
    }
  })
  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <HStack>
          <Button
            isLoading={isSubmitting && !!customer}
            type="submit"
            width="full"
            variant="thin"
            minHeight="3.125rem"
          >
            {t('NOTIFY_ABOUT_AVAILABILITY')}
          </Button>
          {children}
        </HStack>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalBody p="24px">
              <VStack spacing="30px">
                <Text fontSize="18px" fontWeight={400} textAlign="center">
                  {t('PROVIDE_EMAIL_TO_GET_AVAILABILITY_NOTIFICATIONS')}
                </Text>
                <form>
                  <VStack spacing="30px">
                    <Input
                      placeholder={t('ENTER_EMAIL')}
                      name="email"
                      label={t('EMAIL_ADDRESS')}
                    />
                    <Button type="submit" isLoading={isSubmitting}>
                      {t('NOTIFY')}
                    </Button>
                  </VStack>
                </form>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </form>
    </FormProvider>
  )
}

export default ProductNotification
